import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  CalendarIcon,
  UserGroupIcon,
  ShoppingBagIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';

interface FilterProps {
  dateRange: {
    start: string;
    end: string;
    preset: string;
  };
  departments: string[];
  metrics: string[];
  detailLevel: string;
  displayType: string;
  currency: string;
}

interface RevenueAnalysisProps {
  filters: FilterProps;
}

const RevenueAnalysis: React.FC<RevenueAnalysisProps> = ({ filters }) => {
  const { t, direction } = useLanguage();
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [selectedSegment, setSelectedSegment] = useState('all');

  // Revenue summary metrics
  const revenueSummary = [
    {
      title: 'إجمالي الإيرادات',
      value: '2,847,500',
      change: '+12.5%',
      trend: 'up',
      icon: CurrencyDollarIcon,
      color: 'from-green-500 to-green-600'
    },
    {
      title: 'متوسط الإيرادات الشهرية',
      value: '474,583',
      change: '+8.3%',
      trend: 'up',
      icon: CalendarIcon,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'معدل النمو السنوي',
      value: '18.7%',
      change: '+2.1%',
      trend: 'up',
      icon: ArrowTrendingUpIcon,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'عدد العملاء النشطين',
      value: '1,247',
      change: '+15.2%',
      trend: 'up',
      icon: UserGroupIcon,
      color: 'from-orange-500 to-orange-600'
    }
  ];

  // Monthly revenue data
  const monthlyRevenueData = [
    { month: 'يناير', revenue: 2100000, target: 2000000, growth: 5.2, customers: 980 },
    { month: 'فبراير', revenue: 2300000, target: 2100000, growth: 9.5, customers: 1050 },
    { month: 'مارس', revenue: 2500000, target: 2200000, growth: 8.7, customers: 1120 },
    { month: 'أبريل', revenue: 2400000, target: 2300000, growth: -4.0, customers: 1080 },
    { month: 'مايو', revenue: 2600000, target: 2400000, growth: 8.3, customers: 1180 },
    { month: 'يونيو', revenue: 2847500, target: 2500000, growth: 9.5, customers: 1247 }
  ];

  // Revenue by source
  const revenueBySource = [
    { source: 'المبيعات المباشرة', amount: 1423750, percentage: 50, color: '#3B82F6' },
    { source: 'التجارة الإلكترونية', amount: 853125, percentage: 30, color: '#10B981' },
    { source: 'الشراكات', amount: 284375, percentage: 10, color: '#F59E0B' },
    { source: 'الخدمات الاستشارية', amount: 171225, percentage: 6, color: '#EF4444' },
    { source: 'أخرى', amount: 115025, percentage: 4, color: '#8B5CF6' }
  ];

  // Revenue by customer segment
  const customerSegments = [
    { segment: 'الشركات الكبيرة', revenue: 1423750, customers: 45, avgValue: 31639, color: '#3B82F6' },
    { segment: 'الشركات المتوسطة', revenue: 853125, customers: 180, avgValue: 4740, color: '#10B981' },
    { segment: 'الشركات الصغيرة', revenue: 426563, customers: 520, avgValue: 820, color: '#F59E0B' },
    { segment: 'الأفراد', revenue: 143962, customers: 502, avgValue: 287, color: '#EF4444' }
  ];

  // Geographic revenue distribution
  const geographicData = [
    { region: 'الرياض', revenue: 1138000, percentage: 40, growth: '+15%' },
    { region: 'جدة', revenue: 854250, percentage: 30, growth: '+12%' },
    { region: 'الدمام', revenue: 426125, percentage: 15, growth: '+8%' },
    { region: 'المدينة المنورة', revenue: 284750, percentage: 10, growth: '+18%' },
    { region: 'أخرى', revenue: 144375, percentage: 5, growth: '+22%' }
  ];

  // Product/Service performance
  const productPerformance = [
    { product: 'البرمجيات المالية', revenue: 1138000, units: 450, avgPrice: 2529, growth: '+25%' },
    { product: 'الاستشارات المالية', revenue: 854250, units: 180, avgPrice: 4746, growth: '+18%' },
    { product: 'التدريب والتطوير', revenue: 426125, units: 320, avgPrice: 1332, growth: '+12%' },
    { product: 'الدعم التقني', revenue: 284750, units: 890, avgPrice: 320, growth: '+8%' },
    { product: 'التكامل والتنفيذ', revenue: 144375, units: 25, avgPrice: 5775, growth: '+35%' }
  ];

  // Channel performance
  const channelData = [
    { channel: 'الموقع الإلكتروني', revenue: 1138000, percentage: 40, conversion: 3.2 },
    { channel: 'فريق المبيعات', revenue: 854250, percentage: 30, conversion: 15.8 },
    { channel: 'الشركاء', revenue: 426125, percentage: 15, conversion: 8.5 },
    { channel: 'وسائل التواصل', revenue: 284750, percentage: 10, conversion: 2.1 },
    { channel: 'المعارض والفعاليات', revenue: 144375, percentage: 5, conversion: 12.3 }
  ];

  return (
    <div className="space-y-6">
      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {revenueSummary.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <motion.div
              key={metric.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                    <div className={`w-10 h-10 bg-gradient-to-r ${metric.color} rounded-lg p-2`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                    {metric.title}
                  </h3>
                  
                  <div className="flex items-baseline space-x-2 rtl:space-x-reverse mb-2">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {metric.value.toLocaleString()}
                    </span>
                    {metric.title.includes('الإيرادات') && (
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        ر.س
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                      metric.trend === 'up' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {metric.trend === 'up' ? (
                        <ArrowTrendingUpIcon className="w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1" />
                      ) : (
                        <ArrowTrendingUpIcon className="w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1 rotate-180" />
                      )}
                      {metric.change}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Revenue Trend Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue vs Target */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              الإيرادات مقابل المستهدف
            </h3>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="monthly">شهرياً</option>
              <option value="quarterly">ربع سنوي</option>
              <option value="yearly">سنوياً</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={monthlyRevenueData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                yAxisId="revenue"
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}م`}
              />
              <YAxis 
                yAxisId="growth"
                orientation="right"
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                formatter={(value: any, name: any) => {
                  if (name === 'معدل النمو') return [`${value}%`, name];
                  return [`${(value / 1000).toLocaleString()} ألف ر.س`, name];
                }}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar yAxisId="revenue" dataKey="revenue" fill="#3B82F6" name="الإيرادات الفعلية" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="revenue" dataKey="target" fill="#E5E7EB" name="المستهدف" radius={[4, 4, 0, 0]} />
              <Line yAxisId="growth" type="monotone" dataKey="growth" stroke="#10B981" strokeWidth={3} name="معدل النمو" />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Revenue by Source */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            الإيرادات حسب المصدر
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={revenueBySource}
                cx="50%"
                cy="50%"
                outerRadius={100}
                dataKey="amount"
                label={({ source, percentage }) => `${source}: ${percentage}%`}
                labelLine={false}
              >
                {revenueBySource.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, 'المبلغ']}
              />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Customer Segments Analysis */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          تحليل شرائح العملاء
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الشريحة</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الإيرادات</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">عدد العملاء</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">متوسط القيمة</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">النسبة</th>
              </tr>
            </thead>
            <tbody>
              {customerSegments.map((segment, index) => (
                <motion.tr
                  key={segment.segment}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3 rtl:space-x-reverse">
                      <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: segment.color }}></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{segment.segment}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-medium">
                    {(segment.revenue / 1000).toLocaleString()} ألف ر.س
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {segment.customers.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {segment.avgValue.toLocaleString()} ر.س
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full"
                          style={{ 
                            width: `${(segment.revenue / 2847500) * 100}%`,
                            backgroundColor: segment.color 
                          }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {((segment.revenue / 2847500) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Geographic and Channel Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geographic Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <GlobeAltIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
            التوزيع الجغرافي
          </h3>
          <div className="space-y-4">
            {geographicData.map((region, index) => (
              <motion.div
                key={region.region}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {region.region}
                    </span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {(region.revenue / 1000).toLocaleString()} ألف ر.س
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2 ml-3 rtl:ml-0 rtl:mr-3">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-1000"
                        style={{ width: `${region.percentage}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {region.percentage}%
                      </span>
                      <span className="text-xs font-medium text-green-600 dark:text-green-400">
                        {region.growth}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Channel Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <DevicePhoneMobileIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
            أداء القنوات
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={channelData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                type="number"
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000).toLocaleString()}ك`}
              />
              <YAxis 
                type="category"
                dataKey="channel"
                className="text-xs"
                tick={{ fontSize: 12 }}
                width={120}
              />
              <Tooltip
                formatter={(value: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, 'الإيرادات']}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="revenue" fill="#3B82F6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    </div>
  );
};

export default RevenueAnalysis;
